import React, { useState } from 'react';
import { registerUser } from '../config/firebase';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../config/firebase';
import { useNavigate } from 'react-router-dom';

const initialState = {
  fullName: '',
  email: '',
  phone: '',
  dob: '',
  professionalTitle: '',
  licenseId: '',
  facilityName: '',
  community: '',
  experience: '',
  password: '',
  confirmPassword: '',
  terms: false,
};

const initialErrors = {
  fullName: '',
  email: '',
  phone: '',
  dob: '',
  professionalTitle: '',
  licenseId: '',
  facilityName: '',
  community: '',
  experience: '',
  password: '',
  confirmPassword: '',
  terms: '',
};

const RegisterPage = () => {
  const [form, setForm] = useState(initialState);
  const [errors, setErrors] = useState(initialErrors);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const navigate = useNavigate();

  const validate = () => {
    const newErrors = { ...initialErrors };
    let valid = true;
    if (!form.fullName.trim()) {
      newErrors.fullName = 'Full Name is required.';
      valid = false;
    }
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = 'Please enter a valid email address.';
      valid = false;
    }
    if (!form.phone.trim()) {
      newErrors.phone = 'Phone number is required.';
      valid = false;
    }
    if (!form.dob.trim()) {
      newErrors.dob = 'Date of Birth is required.';
      valid = false;
    }
    if (!form.professionalTitle.trim()) {
      newErrors.professionalTitle = 'Please select your title.';
      valid = false;
    }
    if (!form.licenseId.trim()) {
      newErrors.licenseId = 'License ID is required.';
      valid = false;
    }
    if (!form.facilityName.trim()) {
      newErrors.facilityName = 'Facility name is required.';
      valid = false;
    }
    if (!form.community.trim()) {
      newErrors.community = 'Community/District is required.';
      valid = false;
    }
    if (!form.experience.trim()) {
      newErrors.experience = 'Please enter your years of experience.';
      valid = false;
    }
    if (!form.password.match(/^(?=.*\d).{8,}$/)) {
      newErrors.password = 'Password must be at least 8 characters and include at least one number.';
      valid = false;
    }
    if (form.confirmPassword !== form.password) {
      newErrors.confirmPassword = 'Passwords do not match.';
      valid = false;
    }
    if (!form.terms) {
      newErrors.terms = 'You must agree to the terms.';
      valid = false;
    }
    setErrors(newErrors);
    return valid;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    let fieldValue: string | boolean = value;
    if (type === 'checkbox') {
      fieldValue = (e.target as HTMLInputElement).checked;
    }
    setForm(prev => ({
      ...prev,
      [name]: fieldValue,
    }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      setMessage({ text: 'Please correct the errors in the form.', type: 'error' });
      return;
    }
    setSubmitting(true);
    setMessage({ text: '', type: '' });
    try {
      const { password, confirmPassword, terms, ...userFields } = form;
      const userData = {
        ...userFields,
        role: 'Trainee',
        password: form.password,
      };
      const result = await registerUser(userData);
      if (result.success) {
        // Auto-login after registration
        await signInWithEmailAndPassword(auth, form.email, form.password);
        setMessage({ text: 'Registration Successful! Redirecting...', type: 'success' });
        setForm(initialState);
        setTimeout(() => {
          setMessage({ text: '', type: '' });
          navigate('/');
        }, 1000);
      } else {
        let errorMsg = 'Registration failed. Please try again.';
        const error = result.error as any;
        if (error?.code === 'auth/email-already-in-use') {
          errorMsg = 'Email is already in use.';
        } else if (error?.message) {
          errorMsg = error.message;
        }
        setMessage({ text: errorMsg, type: 'error' });
      }
    } catch (err) {
      setMessage({ text: 'Registration failed. Please try again.', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="registration-container">
      <div className="registration-header">
        <h1>Create Your HSI Account</h1>
        <p>Join our mission to build healthier smiles across Sierra Leone.</p>
      </div>
      {message.text && (
        <div className={`message-box show ${message.type}`}>{message.text}</div>
      )}
      <form className="registration-form" onSubmit={handleSubmit} noValidate>
        <fieldset>
          <legend>1. Personal Information</legend>
          <div className="form-grid">
            <div className="form-group full-width">
              <label htmlFor="fullName">Full Name</label>
              <input type="text" id="fullName" name="fullName" value={form.fullName} onChange={handleChange} required disabled={submitting} />
              {errors.fullName && <div className="error-message">{errors.fullName}</div>}
            </div>
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input type="email" id="email" name="email" value={form.email} onChange={handleChange} required disabled={submitting} />
              {errors.email && <div className="error-message">{errors.email}</div>}
            </div>
            <div className="form-group">
              <label htmlFor="phone">Phone Number</label>
              <input type="tel" id="phone" name="phone" value={form.phone} onChange={handleChange} required disabled={submitting} />
              {errors.phone && <div className="error-message">{errors.phone}</div>}
            </div>
            <div className="form-group">
              <label htmlFor="dob">Date of Birth</label>
              <input type="date" id="dob" name="dob" value={form.dob} onChange={handleChange} required disabled={submitting} />
              {errors.dob && <div className="error-message">{errors.dob}</div>}
            </div>
          </div>
        </fieldset>
        <fieldset>
          <legend>2. Professional Details</legend>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="professionalTitle">Professional Title</label>
              <select id="professionalTitle" name="professionalTitle" value={form.professionalTitle} onChange={handleChange} required disabled={submitting}>
                <option value="" disabled>Select your title...</option>
                <option value="Community Health Officer">Community Health Professionals</option>
                <option value="Nurse">Nurse</option>
                <option value="Other">Other</option>
              </select>
              {errors.professionalTitle && <div className="error-message">{errors.professionalTitle}</div>}
            </div>
            <div className="form-group">
              <label htmlFor="licenseId">Professional License/Registration ID</label>
              <input type="text" id="licenseId" name="licenseId" value={form.licenseId} onChange={handleChange} required disabled={submitting} />
              {errors.licenseId && <div className="error-message">{errors.licenseId}</div>}
            </div>
            <div className="form-group">
              <label htmlFor="facilityName">Current Health Facility Name</label>
              <input type="text" id="facilityName" name="facilityName" value={form.facilityName} onChange={handleChange} required disabled={submitting} />
              {errors.facilityName && <div className="error-message">{errors.facilityName}</div>}
            </div>
            <div className="form-group">
              <label htmlFor="community">Community/District of Operation</label>
              <input type="text" id="community" name="community" value={form.community} onChange={handleChange} required disabled={submitting} />
              {errors.community && <div className="error-message">{errors.community}</div>}
            </div>
            <div className="form-group full-width">
              <label htmlFor="experience">Years of Professional Experience</label>
              <input type="number" id="experience" name="experience" value={form.experience} onChange={handleChange} min="0" required disabled={submitting} />
              {errors.experience && <div className="error-message">{errors.experience}</div>}
            </div>
          </div>
        </fieldset>
        <fieldset>
          <legend>3. Account Security</legend>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input type="password" id="password" name="password" value={form.password} onChange={handleChange} required pattern="(?=.*\d).{8,}" disabled={submitting} />
              {errors.password && <div className="error-message">{errors.password}</div>}
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input type="password" id="confirmPassword" name="confirmPassword" value={form.confirmPassword} onChange={handleChange} required disabled={submitting} />
              {errors.confirmPassword && <div className="error-message">{errors.confirmPassword}</div>}
            </div>
          </div>
        </fieldset>
        <div className="terms-group">
          <input type="checkbox" id="terms" name="terms" checked={form.terms} onChange={handleChange} required disabled={submitting} />
          <label htmlFor="terms">I agree to the <a href="#" target="_blank">Terms of Service</a> and <a href="#" target="_blank">Privacy Policy</a>.</label>
          {errors.terms && <div className="error-message" style={{ marginLeft: 8 }}>{errors.terms}</div>}
        </div>
        <button type="submit" className="submit-btn" disabled={submitting || !form.terms}>
          {submitting ? 'Creating Account...' : 'Create Account'}
        </button>
      </form>
      <p className="login-link">
        Already have an account? <a href="/login">Log In</a>
      </p>
      <style>{`
        :root {
          --primary-color: #00796b;
          --secondary-color: #f9a825;
          --background-color: #f4f6f8;
          --form-background: #ffffff;
          --text-color: #333333;
          --light-gray: #e0e0e0;
          --error-color: #c62828;
          --success-color: #2e7d32;
        }
        body { background: none; }
        .registration-container {
          background-color: var(--form-background);
          padding: 30px 40px;
          border-radius: 12px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
          width: 100%;
          max-width: 700px;
          box-sizing: border-box;
          margin: 40px auto;
        }
        .registration-header { text-align: center; margin-bottom: 30px; }
        .registration-header h1 { color: var(--primary-color); margin: 0 0 10px 0; font-size: 28px; }
        .registration-header p { color: var(--text-color); margin: 0; font-size: 16px; }
        .registration-form fieldset { border: 1px solid var(--light-gray); border-radius: 8px; padding: 20px; margin-bottom: 20px; }
        .registration-form legend { padding: 0 10px; font-weight: 600; color: var(--primary-color); }
        .form-grid { display: grid; grid-template-columns: 1fr; gap: 20px; }
        @media (min-width: 600px) { .form-grid { grid-template-columns: 1fr 1fr; } .full-width { grid-column: 1 / -1; } }
        .form-group { display: flex; flex-direction: column; }
        .form-group label { margin-bottom: 8px; font-weight: 600; font-size: 14px; color: var(--text-color); }
        .form-group input, .form-group select { padding: 12px 15px; border: 1px solid var(--light-gray); border-radius: 6px; font-size: 16px; transition: border-color 0.3s, box-shadow 0.3s; width: 100%; box-sizing: border-box; }
        .form-group input:focus, .form-group select:focus { outline: none; border-color: var(--primary-color); box-shadow: 0 0 0 3px rgba(0, 121, 107, 0.2); }
        .error-message { color: var(--error-color); font-size: 12px; margin-top: 5px; }
        .terms-group { display: flex; align-items: center; margin: 20px 0; font-size: 14px; }
        .terms-group input { margin-right: 10px; }
        .terms-group a { color: var(--primary-color); text-decoration: none; font-weight: 600; }
        .terms-group a:hover { text-decoration: underline; }
        .submit-btn { width: 100%; padding: 15px; background-color: var(--primary-color); color: white; border: none; border-radius: 6px; font-size: 18px; font-weight: 600; cursor: pointer; transition: background-color 0.3s, transform 0.2s; }
        .submit-btn:hover { background-color: #005a4f; }
        .submit-btn:active { transform: scale(0.98); }
        .submit-btn:disabled { background-color: #9e9e9e; cursor: not-allowed; }
        .login-link { text-align: center; margin-top: 20px; font-size: 14px; }
        .login-link a { color: var(--primary-color); font-weight: 600; text-decoration: none; }
        .login-link a:hover { text-decoration: underline; }
        .message-box { position: fixed; top: 20px; right: 20px; padding: 15px 20px; border-radius: 8px; color: white; font-size: 16px; z-index: 1000; opacity: 0; transform: translateY(-50px); transition: opacity 0.5s, transform 0.5s; }
        .message-box.show { opacity: 1; transform: translateY(0); }
        .message-box.success { background-color: var(--success-color); }
        .message-box.error { background-color: var(--error-color); }
      `}</style>
    </div>
  );
};

export default RegisterPage; 